import type { SentimentDistribution } from '@/types/report';

interface KeyIndicatorsCardsProps {
  reliabilityScore: number;
  overallAverage: number;
  confidenceScore: number;
  sentimentDistribution: SentimentDistribution;
  hasEnoughDataForScores?: boolean; // ✅ NOUVEAU : Flag pour afficher "Aucune donnée éligible"
}

/**
 * Composant affichant 4 cartes d'indicateurs clés :
 * - Fiabilité globale
 * - Moyenne générale
 * - Confiance
 * - Sentiment global (gauge en demi-cercle)
 */
export const KeyIndicatorsCards = ({
  reliabilityScore,
  overallAverage,
  confidenceScore,
  sentimentDistribution,
  hasEnoughDataForScores = true, // ✅ Par défaut true pour rétrocompatibilité
}: KeyIndicatorsCardsProps) => {
  // 🔍 DEBUG : Logs pour diagnostiquer le problème d'affichage
  console.log('🔍 DEBUG KeyIndicatorsCards - Props reçues:', {
    reliabilityScore,
    overallAverage,
    confidenceScore,
    sentimentDistribution,
    sentimentTotal: sentimentDistribution.negative + sentimentDistribution.neutral + sentimentDistribution.positive
  });

  // Fonction pour déterminer la couleur selon le score
  const getScoreColor = (score: number, isPercentage = true): string => {
    const threshold = isPercentage ? 70 : 7;
    if (score >= threshold) return 'text-green-500';
    if (score >= (isPercentage ? 50 : 5)) return 'text-yellow-400';
    return 'text-rose-500';
  };

  // Générer le gauge en demi-cercle (compact)
  const generateGaugeChart = () => {
    const { negative, neutral, positive } = sentimentDistribution;
    const total = negative + neutral + positive;
    
    if (total === 0) {
      return (
        <svg width="140" height="80" viewBox="0 0 140 80" className="mx-auto">
          <path
            d="M 20 70 A 50 50 0 0 1 120 70"
            fill="none"
            stroke="#E5E7EB"
            strokeWidth="12"
            strokeLinecap="round"
          />
        </svg>
      );
    }

    // Calculer les angles pour chaque segment (en degrés, sur 180° pour le demi-cercle)
    const negativeAngle = (negative / total) * 180;
    const neutralAngle = (neutral / total) * 180;
    const positiveAngle = (positive / total) * 180;

    // Fonction pour créer un arc SVG en demi-cercle
    const createArc = (startAngle: number, endAngle: number, color: string) => {
      // Convertir les angles (0° = gauche, 180° = droite)
      const start = polarToCartesian(70, 70, 50, 180 - startAngle);
      const end = polarToCartesian(70, 70, 50, 180 - endAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
      
      return (
        <path
          key={`${startAngle}-${endAngle}-${color}`}
          d={`M ${start.x} ${start.y} A 50 50 0 ${largeArcFlag} 1 ${end.x} ${end.y}`}
          fill="none"
          stroke={color}
          strokeWidth="12"
          strokeLinecap="round"
        />
      );
    };

    // Convertir coordonnées polaires en cartésiennes
    const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
      const angleInRadians = (angleInDegrees * Math.PI) / 180.0;
      return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY - radius * Math.sin(angleInRadians),
      };
    };

    const arcs = [];
    let currentAngle = 0;

    // Arc rouge (négatif) - commence à gauche
    if (negative > 0) {
      arcs.push(createArc(currentAngle, currentAngle + negativeAngle, '#FF515D'));
      currentAngle += negativeAngle;
    }

    // Arc jaune (neutre)
    if (neutral > 0) {
      arcs.push(createArc(currentAngle, currentAngle + neutralAngle, '#FFC506'));
      currentAngle += neutralAngle;
    }

    // Arc vert (positif) - termine à droite
    if (positive > 0) {
      arcs.push(createArc(currentAngle, currentAngle + positiveAngle, '#32CD32'));
    }

    return (
      <svg width="140" height="80" viewBox="0 0 140 80" className="mx-auto">
        {arcs}
      </svg>
    );
  };

  return (
    <div className="flex gap-5 w-full">
      {/* Carte 1 : Fiabilité globale */}
      <div className="flex-1 h-36 p-4 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-zinc-200 flex flex-col justify-center items-center gap-3">
        <div className="text-center text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
          <span className="uppercase">F</span>
          <span className="lowercase">iabilité globale</span>
        </div>
        {hasEnoughDataForScores ? (
          <>
            <div className={`text-center text-2xl font-semibold font-['Bricolage_Grotesque'] ${getScoreColor(reliabilityScore)}`}>
              {reliabilityScore}%
            </div>
            <div className="text-center text-neutral-400 text-[10px] font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
              Score de confiance
            </div>
          </>
        ) : (
          <div className="text-center text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
            Aucune donnée éligible
          </div>
        )}
      </div>

      {/* Carte 2 : Moyenne générale */}
      <div className="flex-1 h-36 p-4 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-zinc-200 flex flex-col justify-center items-center gap-3">
        <div className="text-center text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
          <span className="uppercase">M</span>
          <span className="lowercase">oyenne générale</span>
        </div>
        {hasEnoughDataForScores ? (
          <>
            <div className={`text-center text-2xl font-semibold font-['Bricolage_Grotesque'] ${getScoreColor(overallAverage, false)}`}>
              {overallAverage.toFixed(1)}/10
            </div>
            <div className="text-center text-neutral-400 text-[10px] font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
              Évaluation compétences
            </div>
          </>
        ) : (
          <div className="text-center text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
            Aucune donnée éligible
          </div>
        )}
      </div>

      {/* Carte 3 : Confiance */}
      <div className="flex-1 h-36 p-4 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-zinc-200 flex flex-col justify-center items-center gap-3">
        <div className="text-center text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
          <span className="uppercase">C</span>
          <span className="lowercase">onfiance</span>
        </div>
        <div className={`text-center text-2xl font-semibold font-['Bricolage_Grotesque'] ${getScoreColor(confidenceScore)}`}>
          {confidenceScore}%
        </div>
        <div className="text-center text-neutral-400 text-[10px] font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
          Qualité des réponses
        </div>
      </div>

      {/* Carte 4 : Sentiment Global avec Gauge en demi-cercle */}
      <div className="flex-1 h-36 p-4 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-zinc-200 flex flex-col justify-end items-center gap-3">
        {/* Gauge Chart en demi-cercle - réduit */}
        <div className="w-full flex justify-center -mb-2">
          {generateGaugeChart()}
        </div>

        {/* Légende avec pourcentages et labels - compacte */}
        <div className="self-stretch flex flex-col justify-start items-center gap-[2px]">
          <div className="self-stretch px-3 flex justify-between items-center">
            <div className="flex justify-start items-center gap-0.5">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="10" height="10" rx="3" fill="#FF515D"/>
              </svg>
              <div className="text-center text-gray-800 text-[10px] font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
                {sentimentDistribution.negative}%
              </div>
            </div>

            <div className="flex justify-start items-center gap-0.5">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="10" height="10" rx="3" fill="#FFC506"/>
              </svg>
              <div className="text-center text-gray-800 text-[10px] font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
                {sentimentDistribution.neutral}%
              </div>
            </div>

            <div className="flex justify-start items-center gap-0.5">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="10" height="10" rx="3" fill="#32CD32"/>
              </svg>
              <div className="text-center text-gray-800 text-[10px] font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
                {sentimentDistribution.positive}%
              </div>
            </div>
          </div>

          <div className="self-stretch px-3 flex justify-between items-center">
            <div className="text-center text-neutral-400 text-[10px] font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
              négatif
            </div>
            <div className="text-center text-neutral-400 text-[10px] font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
              neutre
            </div>
            <div className="text-center text-neutral-400 text-[10px] font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
              positif
            </div>
          </div>

          <div className="self-stretch text-center text-neutral-400 text-[10px] font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
            Sentiment global
          </div>
        </div>
      </div>
    </div>
  );
};
